import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type PickerView = 'day' | 'month' | 'year';

type CalendarDay = {
  date: Date;
  isoDate: string;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  isDisabled: boolean;
};

export type TailwindDateFieldProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'defaultValue' | 'onChange'
> & {
  value?: string;
  defaultValue?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onValueChange?: (value: string) => void;
  wrapperClassName?: string;
  centered?: boolean;
};

const FIELD_CLASS =
  'block w-full ps-9 pe-3 py-2.5 bg-neutral-secondary-medium border border-default-medium text-heading text-sm rounded-base focus:ring-brand focus:border-brand px-3 py-2.5 shadow-xs placeholder:text-body';
const ICON_HTML =
  '<svg class="w-4 h-4 text-body" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 10h16m-8-3V4M7 7V4m10 3V4M5 20h14a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Zm3-7h.01v.01H8V13Zm4 0h.01v.01H12V13Zm4 0h.01v.01H16V13Zm-8 4h.01v.01H8V17Zm4 0h.01v.01H12V17Zm4 0h.01v.01H16V17Z"/></svg>';
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MONTH_LABEL_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const US_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const today = (): Date => startOfDay(new Date());

const isValidDate = (
  date: Date,
  year: number,
  month: number,
  day: number,
): boolean =>
  date.getFullYear() === year
  && date.getMonth() === month
  && date.getDate() === day;

const parseIsoDate = (value: string | null | undefined): Date | null => {
  const match = String(value ?? '').trim().match(ISO_DATE_PATTERN);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, month, day);
  return isValidDate(parsed, year, month, day) ? startOfDay(parsed) : null;
};

const parseDisplayDate = (value: string | null | undefined): Date | null => {
  const raw = String(value ?? '').trim();
  const iso = parseIsoDate(raw);
  if (iso) return iso;

  const match = raw.match(US_DATE_PATTERN);
  if (!match) return null;
  const month = Number(match[1]) - 1;
  const day = Number(match[2]);
  const year = Number(match[3]);
  const parsed = new Date(year, month, day);
  return isValidDate(parsed, year, month, day) ? startOfDay(parsed) : null;
};

const formatIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
};

const monthStart = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date: Date, offset: number): Date =>
  new Date(date.getFullYear(), date.getMonth() + offset, 1);

const addYears = (date: Date, offset: number): Date =>
  new Date(date.getFullYear() + offset, date.getMonth(), 1);

const sameDay = (left: Date | null, right: Date | null): boolean =>
  Boolean(
    left
      && right
      && left.getFullYear() === right.getFullYear()
      && left.getMonth() === right.getMonth()
      && left.getDate() === right.getDate(),
  );

const sameMonth = (left: Date | null, year: number, month: number): boolean =>
  Boolean(left && left.getFullYear() === year && left.getMonth() === month);

const isBefore = (left: Date, right: Date): boolean =>
  startOfDay(left).getTime() < startOfDay(right).getTime();

const isAfter = (left: Date, right: Date): boolean =>
  startOfDay(left).getTime() > startOfDay(right).getTime();

const isWithinRange = (
  date: Date,
  minDate: Date | null,
  maxDate: Date | null,
): boolean =>
  !(minDate && isBefore(date, minDate))
  && !(maxDate && isAfter(date, maxDate));

const getMonthEnd = (year: number, month: number): Date =>
  new Date(year, month + 1, 0);

const getYearEnd = (year: number): Date => new Date(year, 11, 31);

const isMonthDisabled = (
  year: number,
  month: number,
  minDate: Date | null,
  maxDate: Date | null,
): boolean => {
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = getMonthEnd(year, month);
  return Boolean(
    (maxDate && isAfter(firstOfMonth, maxDate))
      || (minDate && isBefore(lastOfMonth, minDate)),
  );
};

const isYearDisabled = (
  year: number,
  minDate: Date | null,
  maxDate: Date | null,
): boolean => {
  const firstOfYear = new Date(year, 0, 1);
  const lastOfYear = getYearEnd(year);
  return Boolean(
    (maxDate && isAfter(firstOfYear, maxDate))
      || (minDate && isBefore(lastOfYear, minDate)),
  );
};

const getDecadeStart = (year: number): number => Math.floor(year / 10) * 10;

const buildCalendarDays = ({
  selectedDate,
  visibleMonth,
  minDate,
  maxDate,
}: {
  selectedDate: Date | null;
  visibleMonth: Date;
  minDate: Date | null;
  maxDate: Date | null;
}): CalendarDay[] => {
  const firstOfMonth = monthStart(visibleMonth);
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(firstOfMonth.getDate() - firstOfMonth.getDay());
  const currentToday = today();

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    const normalized = startOfDay(date);
    const isDisabled = !isWithinRange(normalized, minDate, maxDate);

    return {
      date: normalized,
      isoDate: formatIsoDate(normalized),
      isCurrentMonth: normalized.getMonth() === firstOfMonth.getMonth(),
      isSelected: sameDay(selectedDate, normalized),
      isToday: sameDay(currentToday, normalized),
      isDisabled,
    };
  });
};

const buildChangeEvent = (
  element: HTMLInputElement | null,
): React.ChangeEvent<HTMLInputElement> =>
  ({
    target: element,
    currentTarget: element,
    bubbles: true,
    cancelable: false,
    defaultPrevented: false,
    eventPhase: 3,
    isTrusted: false,
    nativeEvent: new Event('change'),
    preventDefault() {},
    isDefaultPrevented: () => false,
    stopPropagation() {},
    isPropagationStopped: () => false,
    persist() {},
    timeStamp: Date.now(),
    type: 'change',
  } as React.ChangeEvent<HTMLInputElement>);

const buildBlurEvent = (
  element: HTMLInputElement | null,
): React.FocusEvent<HTMLInputElement> =>
  ({
    target: element,
    currentTarget: element,
    bubbles: true,
    cancelable: false,
    defaultPrevented: false,
    eventPhase: 3,
    isTrusted: false,
    nativeEvent: new FocusEvent('blur'),
    relatedTarget: document.activeElement,
    preventDefault() {},
    isDefaultPrevented: () => false,
    stopPropagation() {},
    isPropagationStopped: () => false,
    persist() {},
    timeStamp: Date.now(),
    type: 'blur',
  } as React.FocusEvent<HTMLInputElement>);

export const TailwindDateField = forwardRef<
  HTMLInputElement,
  TailwindDateFieldProps
>(function TailwindDateField(
  {
    value,
    defaultValue,
    onChange,
    onValueChange,
    onBlur,
    onFocus,
    className: _className = '',
    wrapperClassName = '',
    centered: _centered = false,
    placeholder = 'Select date',
    min,
    max,
    disabled,
    readOnly,
    required,
    name,
    id,
    autoComplete = 'off',
    'aria-label': ariaLabel,
    style: _style,
    ...rest
  },
  forwardedRef,
) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? '');
  const committedValue = isControlled ? value ?? '' : internalValue;
  const [displayValue, setDisplayValue] = useState(() => {
    const parsed = parseIsoDate(committedValue);
    return parsed ? formatDisplayDate(parsed) : committedValue;
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pickerView, setPickerView] = useState<PickerView>('day');
  const minValue =
    typeof min === 'string' ? min : min === undefined ? undefined : String(min);
  const maxValue =
    typeof max === 'string' ? max : max === undefined ? undefined : String(max);
  const [visibleMonth, setVisibleMonth] = useState<Date>(() =>
    monthStart(parseIsoDate(committedValue) ?? parseIsoDate(minValue) ?? today()),
  );
  const [popupRect, setPopupRect] = useState<DOMRect | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const eventRef = useRef<HTMLInputElement | null>(null);

  useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement);

  const selectedDate = useMemo(
    () => parseIsoDate(committedValue),
    [committedValue],
  );
  const minDate = useMemo(() => parseIsoDate(minValue), [minValue]);
  const maxDate = useMemo(() => parseIsoDate(maxValue), [maxValue]);

  useEffect(() => {
    if (eventRef.current) {
      eventRef.current.value = committedValue;
    }
    if (!isDirty) {
      const parsed = parseIsoDate(committedValue);
      setDisplayValue(parsed ? formatDisplayDate(parsed) : committedValue);
    }
  }, [committedValue, isDirty]);

  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.setAttribute('datepicker', '');
    inputRef.current.setAttribute('datepicker-autohide', '');
  }, []);

  const syncPopupRect = useCallback(() => {
    if (!inputRef.current) return;
    setPopupRect(inputRef.current.getBoundingClientRect());
  }, []);

  const openPicker = useCallback(() => {
    if (disabled || readOnly) return;
    setPickerView('day');
    setVisibleMonth(monthStart(selectedDate ?? minDate ?? today()));
    syncPopupRect();
    setIsOpen(true);
  }, [disabled, maxDate, minDate, readOnly, selectedDate, syncPopupRect]);

  const closePicker = useCallback(() => {
    setIsOpen(false);
  }, []);

  const emitChange = useCallback(
    (nextValue: string) => {
      if (!isControlled) {
        setInternalValue(nextValue);
      }
      if (eventRef.current) {
        eventRef.current.value = nextValue;
      }
      onValueChange?.(nextValue);
      if (onChange) {
        onChange(buildChangeEvent(eventRef.current));
      }
    },
    [isControlled, onChange, onValueChange],
  );

  const emitBlur = useCallback(() => {
    onBlur?.(buildBlurEvent(eventRef.current));
  }, [onBlur]);

  const commitTypedValue = useCallback(
    ({ close, emitBlurEvent }: { close: boolean; emitBlurEvent: boolean }) => {
      const rawValue = displayValue.trim();
      let nextValue = committedValue;

      if (!rawValue) {
        nextValue = '';
      } else {
        const parsed = parseDisplayDate(rawValue);
        if (parsed && isWithinRange(parsed, minDate, maxDate)) {
          nextValue = formatIsoDate(parsed);
          setVisibleMonth(monthStart(parsed));
        }
      }

      if (nextValue !== committedValue) {
        emitChange(nextValue);
      }

      const parsedNext = parseIsoDate(nextValue);
      setDisplayValue(parsedNext ? formatDisplayDate(parsedNext) : nextValue);
      setIsDirty(false);

      if (close) {
        closePicker();
      }
      if (emitBlurEvent) {
        emitBlur();
      }
    },
    [
      closePicker,
      committedValue,
      displayValue,
      emitBlur,
      emitChange,
      maxDate,
      minDate,
    ],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (wrapperRef.current?.contains(target)) return;
      if (
        target instanceof Element
        && target.closest('[data-ystnj-tailwind-datepicker-popup="true"]')
      ) {
        return;
      }
      commitTypedValue({ close: true, emitBlurEvent: true });
    };

    const handleWindowChange = () => syncPopupRect();

    document.addEventListener('pointerdown', handleOutsidePointerDown);
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      document.removeEventListener('pointerdown', handleOutsidePointerDown);
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [commitTypedValue, isOpen, syncPopupRect]);

  const calendarDays = useMemo(
    () =>
      buildCalendarDays({
        selectedDate,
        visibleMonth,
        minDate,
        maxDate,
      }),
    [maxDate, minDate, selectedDate, visibleMonth],
  );

  const visibleYear = visibleMonth.getFullYear();
  const visibleMonthIndex = visibleMonth.getMonth();
  const decadeStart = getDecadeStart(visibleYear);

  const titleLabel =
    pickerView === 'day'
      ? MONTH_LABEL_FORMATTER.format(visibleMonth)
      : pickerView === 'month'
        ? String(visibleYear)
        : `${decadeStart}-${decadeStart + 9}`;

  const popupStyle = useMemo((): React.CSSProperties | undefined => {
    if (!popupRect) return undefined;
    return {
      width: Math.max(280, Math.min(popupRect.width || 320, 360)),
    };
  }, [popupRect]);

  const navigatePicker = useCallback(
    (direction: number) => {
      if (pickerView === 'day') {
        setVisibleMonth((current) => addMonths(current, direction));
        return;
      }
      if (pickerView === 'month') {
        setVisibleMonth((current) => addYears(current, direction));
        return;
      }
      setVisibleMonth((current) => addYears(current, direction * 10));
    },
    [pickerView],
  );

  const selectDay = useCallback(
    (day: CalendarDay) => {
      if (day.isDisabled) return;
      emitChange(day.isoDate);
      setDisplayValue(formatDisplayDate(day.date));
      setIsDirty(false);
      closePicker();
      emitBlur();
      inputRef.current?.focus({ preventScroll: true });
    },
    [closePicker, emitBlur, emitChange],
  );

  const selectMonth = useCallback(
    (month: number) => {
      if (isMonthDisabled(visibleYear, month, minDate, maxDate)) return;
      setVisibleMonth(new Date(visibleYear, month, 1));
      setPickerView('day');
    },
    [maxDate, minDate, visibleYear],
  );

  const selectYear = useCallback(
    (year: number) => {
      if (isYearDisabled(year, minDate, maxDate)) return;
      setVisibleMonth(new Date(year, visibleMonthIndex, 1));
      setPickerView('month');
    },
    [maxDate, minDate, visibleMonthIndex],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        const parsed = parseIsoDate(committedValue);
        setDisplayValue(parsed ? formatDisplayDate(parsed) : committedValue);
        setIsDirty(false);
        closePicker();
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        commitTypedValue({ close: false, emitBlurEvent: false });
        openPicker();
        return;
      }
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openPicker();
      }
    },
    [closePicker, commitTypedValue, committedValue, openPicker],
  );

  const renderDayView = () => (
    <>
      <div className="mb-2 grid grid-cols-7 text-center text-sm font-semibold text-[#4b5563]">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-1 text-center">
        {calendarDays.map((day) => {
          const buttonClassName = [
            'mx-auto flex h-8 w-8 items-center justify-center rounded-[12px] text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/25',
            day.isSelected ? 'bg-[#1d4ed8] text-white' : '',
            !day.isSelected && day.isCurrentMonth
              ? 'text-[#4b5563] hover:bg-[#eef2ff]'
              : '',
            !day.isSelected && !day.isCurrentMonth
              ? 'text-[#9ca3af] hover:bg-[#f3f4f6]'
              : '',
            day.isToday && !day.isSelected ? 'ring-1 ring-[#1d4ed8]/30' : '',
            day.isDisabled
              ? 'cursor-not-allowed opacity-40 hover:bg-transparent'
              : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={day.isoDate}
              type="button"
              className={buttonClassName}
              disabled={day.isDisabled}
              aria-label={day.isoDate}
              aria-pressed={day.isSelected}
              onClick={() => selectDay(day)}
            >
              {day.date.getDate()}
            </button>
          );
        })}
      </div>
    </>
  );

  const renderMonthView = () => (
    <div className="grid grid-cols-4 gap-x-2 gap-y-3 py-2 text-center">
      {MONTH_LABELS.map((monthLabel, month) => {
        const isSelected = sameMonth(selectedDate, visibleYear, month);
        const disabledMonth = isMonthDisabled(visibleYear, month, minDate, maxDate);
        const buttonClassName = [
          'mx-auto flex h-9 min-w-14 items-center justify-center rounded-[14px] px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/25',
          isSelected
            ? 'bg-[#1d4ed8] text-white'
            : 'text-[#4b5563] hover:bg-[#eef2ff]',
          disabledMonth
            ? 'cursor-not-allowed opacity-40 hover:bg-transparent'
            : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={monthLabel}
            type="button"
            className={buttonClassName}
            disabled={disabledMonth}
            aria-pressed={isSelected}
            onClick={() => selectMonth(month)}
          >
            {monthLabel}
          </button>
        );
      })}
    </div>
  );

  const renderYearView = () => {
    const years = Array.from({ length: 12 }, (_, index) => decadeStart - 1 + index);

    return (
      <div className="grid grid-cols-4 gap-x-2 gap-y-3 py-2 text-center">
        {years.map((year) => {
          const isSelected = selectedDate?.getFullYear() === year;
          const isCurrentDecade = year >= decadeStart && year <= decadeStart + 9;
          const disabledYear = isYearDisabled(year, minDate, maxDate);
          const buttonClassName = [
            'mx-auto flex h-9 min-w-14 items-center justify-center rounded-[14px] px-3 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/25',
            isSelected ? 'bg-[#1d4ed8] text-white' : '',
            !isSelected && isCurrentDecade
              ? 'text-[#4b5563] hover:bg-[#eef2ff]'
              : '',
            !isSelected && !isCurrentDecade
              ? 'text-[#9ca3af] hover:bg-[#f3f4f6]'
              : '',
            disabledYear
              ? 'cursor-not-allowed opacity-40 hover:bg-transparent'
              : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={year}
              type="button"
              className={buttonClassName}
              disabled={disabledYear}
              aria-pressed={isSelected}
              onClick={() => selectYear(year)}
            >
              {year}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={`relative max-w-sm ${wrapperClassName}`.trim()}
        data-ystnj-tailwind-datepicker-field="true"
      >
        <div
          className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none"
          dangerouslySetInnerHTML={{ __html: ICON_HTML }}
        />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={(event) => {
            setDisplayValue(event.target.value);
            setIsDirty(true);
          }}
          onFocus={(event) => {
            onFocus?.(event);
            openPicker();
          }}
          onClick={() => openPicker()}
          onBlur={() => {
            window.setTimeout(() => {
              if (document.activeElement?.closest('[data-ystnj-tailwind-datepicker-popup="true"]')) {
                return;
              }
              commitTypedValue({ close: true, emitBlurEvent: true });
            }, 0);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode="numeric"
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          aria-label={ariaLabel ?? name ?? 'Select date'}
          className={FIELD_CLASS}
        />
        {isOpen && popupStyle && wrapperRef.current && typeof document !== 'undefined'
          ? createPortal(
            <div
              data-ystnj-tailwind-datepicker-popup="true"
              className="absolute left-0 top-[calc(100%+0.5rem)] z-[9999] rounded-[18px] border border-[#dfe4ea] bg-white p-4 shadow-[0_12px_34px_rgba(15,23,42,0.14)]"
              style={popupStyle}
              role="dialog"
              aria-label="Choose date"
              onMouseDown={(event) => event.preventDefault()}
            >
              <div className="mb-3 grid grid-cols-[36px_1fr_36px] items-center gap-2">
                <button
                  type="button"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-[#4b5563] transition-colors hover:bg-[#f3f6f8] focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/25"
                  aria-label={
                    pickerView === 'year'
                      ? 'Previous decade'
                      : pickerView === 'month'
                        ? 'Previous year'
                        : 'Previous month'
                  }
                  onClick={() => navigatePicker(-1)}
                >
                  <ChevronLeft aria-hidden="true" size={22} strokeWidth={2.75} />
                </button>
                <button
                  type="button"
                  className="min-w-0 rounded-md px-1 py-1 text-center text-base font-semibold text-[#111827] transition-colors hover:bg-[#f3f6f8] focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/25"
                  aria-label={`Select ${
                    pickerView === 'day'
                      ? 'month'
                      : pickerView === 'month'
                        ? 'year'
                        : 'year range'
                  }`}
                  onClick={() => {
                    if (pickerView === 'day') {
                      setPickerView('month');
                      return;
                    }
                    if (pickerView === 'month') {
                      setPickerView('year');
                    }
                  }}
                >
                  {titleLabel}
                </button>
                <button
                  type="button"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-[#4b5563] transition-colors hover:bg-[#f3f6f8] focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/25"
                  aria-label={
                    pickerView === 'year'
                      ? 'Next decade'
                      : pickerView === 'month'
                        ? 'Next year'
                        : 'Next month'
                  }
                  onClick={() => navigatePicker(1)}
                >
                  <ChevronRight aria-hidden="true" size={22} strokeWidth={2.75} />
                </button>
              </div>
              {pickerView === 'day'
                ? renderDayView()
                : pickerView === 'month'
                  ? renderMonthView()
                  : renderYearView()}
            </div>,
            wrapperRef.current,
          )
          : null}
      </div>
      <input
        ref={eventRef}
        {...rest}
        type="date"
        id={id}
        name={name}
        value={committedValue}
        min={minValue}
        max={maxValue}
        disabled={disabled}
        readOnly
        required={required}
        aria-label={ariaLabel ?? name ?? 'Select date'}
        aria-hidden="true"
        tabIndex={-1}
        data-ystnj-datepicker-source="true"
        className={_className}
        style={{ ...(_style as CSSProperties | undefined), display: 'none' }}
        />
    </>
  );
});
